import * as React from 'react';

import {
  enumSalesLoadLineItemPhase,
  enumUnit,
} from '@rematter/frontend-lib-api-client';
import {
  $lineItemsByPhaseKey,
  $loadForm,
  $showSaveSuccess,
} from '@rematter/modules/trade/_state/loadForm.state';
import {
  cn,
  humanReadableNumber,
  humanReadableShortPricingUnit,
  TouchButton,
  Widget,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideEqual, LucideMinus } from 'lucide-react';

import { useLoadLineItemsTotals } from '../../../hooks/useLoadLineItemsTotals';
import { LoadFormState } from '../../../types/load';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

type Props = {
  onSaveUpdates: (
    tab?: 'load-info' | 'materials',
    data?: LoadFormState,
    onlyDatesAndEntities?: boolean,
  ) => void;
  onDiscard: (onlyDiscardDatesAndEntities?: boolean) => void;
  isSaving?: boolean;
  isDisabled?: boolean;
};

type QuantityDisplayProps = {
  value: number;
  unit?: string;
};

const QuantityDisplay: React.FC<QuantityDisplayProps> = ({ value, unit }) => (
  <span className="@QuantityDisplay">
    <span className="font-medium">
      {humanReadableNumber(value, {
        locale: getLocale(),
      })}
    </span>{' '}
    <span className="text-neutral/60">
      {unit && humanReadableShortPricingUnit(unit)}
    </span>
  </span>
);

type FooterSectionProps = {
  label: string;
  weightValue?: number;
  weightUnit?: string;
  eachValue?: number;
};

const FooterSection: React.FC<FooterSectionProps> = ({
  label,
  weightValue,
  weightUnit,
  eachValue,
}) => (
  <div className="@FooterSection flex flex-col items-end gap-1">
    <div className="text-[0.563rem] font-medium text-neutral/60 uppercase">
      {label}
    </div>
    <div className="flex items-center gap-2 text-lg">
      {weightValue ? (
        <QuantityDisplay value={weightValue} unit={weightUnit} />
      ) : null}
      {eachValue ? <QuantityDisplay value={eachValue} unit="Each" /> : null}
      {!weightValue && !eachValue ? (
        <QuantityDisplay value={0} unit={weightUnit} />
      ) : null}
    </div>
  </div>
);

export const LoadStagedFooter: React.FC<Props> = ({
  onSaveUpdates,
  onDiscard,
  isSaving,
  isDisabled,
}) => {
  const t = useTranslations();

  const lineItemsByPhaseKey = useAtomValue($lineItemsByPhaseKey);
  const [form] = useAtom($loadForm);
  const [showSaveSuccess, setShowSaveSuccess] = useAtom($showSaveSuccess);

  const stagedLineItems =
    lineItemsByPhaseKey[enumSalesLoadLineItemPhase.Staged];
  const requestedLineItems =
    lineItemsByPhaseKey[enumSalesLoadLineItemPhase.Requested];

  const facilityDefaultUnit = enumUnit.Pounds;
  const getFooterDisplayUnit = React.useCallback(() => enumUnit.Pounds, []);

  const requestedLineItemsTotals = useLoadLineItemsTotals({
    lineItems: requestedLineItems,
    facilityDefaultUnit,
    getFooterDisplayUnit,
  });

  const stagedLineItemsTotals = useLoadLineItemsTotals({
    lineItems: stagedLineItems,
    facilityDefaultUnit,
    getFooterDisplayUnit,
  });

  React.useEffect(() => {
    if (showSaveSuccess) {
      const timer = setTimeout(() => setShowSaveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSaveSuccess, setShowSaveSuccess]);

  return (
    <Widget className={cn('@LoadStagedFooter', 'flex-shrink-0 px-4 py-3')}>
      <div className="flex items-center justify-end gap-9">
        <div className="flex items-center justify-center gap-5">
          <FooterSection
            label={t.loadStaged.footer.requested}
            weightValue={requestedLineItemsTotals.netWeight?.value ?? 0}
            weightUnit={
              requestedLineItemsTotals.netWeight?.unit ?? enumUnit.Pounds
            }
            eachValue={requestedLineItemsTotals.requestedEachCount}
          />

          <div className="text-2xl text-neutral/40">
            <LucideMinus />
          </div>

          <FooterSection
            label={t.loadStaged.footer.staged}
            weightValue={stagedLineItemsTotals.netWeight?.value ?? 0}
            weightUnit={
              stagedLineItemsTotals.netWeight?.unit ?? enumUnit.Pounds
            }
            eachValue={stagedLineItemsTotals.requestedEachCount}
          />

          <div className="text-2xl text-neutral/40">
            <LucideEqual />
          </div>

          <FooterSection
            label={t.loadStaged.footer.remaining}
            weightValue={
              (requestedLineItemsTotals.netWeight?.value ?? 0) -
              (stagedLineItemsTotals.netWeight?.value ?? 0)
            }
            weightUnit={
              requestedLineItemsTotals.netWeight?.unit ?? enumUnit.Pounds
            }
            eachValue={
              requestedLineItemsTotals.requestedEachCount -
              stagedLineItemsTotals.requestedEachCount
            }
          />
        </div>

        {!isDisabled && (
          <div className="flex items-center gap-3">
            <TouchButton
              variant="outline"
              color="neutral"
              isSelected={false}
              onPress={onDiscard}
              isDisabled={isSaving}
              className="rounded-md border-[2px]"
            >
              {t.loadDetail.footer.discard}
            </TouchButton>

            <TouchButton
              variant="solid"
              color="primary"
              isSelected={false}
              onPress={() => onSaveUpdates('materials', form)}
              isPending={isSaving}
              isSaved={showSaveSuccess}
              className="rounded-md border-[2px]"
            >
              {t.loadDetail.footer.saveUpdates}
            </TouchButton>
          </div>
        )}
      </div>
    </Widget>
  );
};
